import { FC, ReactNode } from 'react';
import { isNil } from 'lodash-es';
import { WarningAltFilled } from '@carbon/react/icons';
import { FloatingTooltip } from 'components/floating-tooltip/floating-tooltip';
import { formatNumber } from 'utils/number';
import styles from './over-allocation-warning.module.scss';

const formatVariable = (value: number) =>
  formatNumber({
    value,
    minPrecision: 0,
    maxPrecision: 8,
    includeCommas: true,
  });

/**
 * Flags a slot-level interest total that has run past 1.0.
 *
 * A slot's total interest across all owners is a fraction of one by definition.
 * Above it, the unit is over-covered — its tracts collectively claim more of the
 * ground than the unit contains (INS-11621). Neither the roll-up in
 * NriCalculation::GroupingAggregator nor the per-record BundleOfSticksOwnership
 * validation catches that, so a 108% working interest would otherwise render
 * exactly like a correct one. Flag it wherever the total is read.
 *
 * Renders nothing at or below 1.0, so it is safe to drop into any Total Slot
 * NRI/WI cell.
 *
 * BEST-EFFORT, and deliberately so. It flags whatever the cell it sits in is
 * displaying, and on surfaces where ONCALL-761 rebound Total Slot NRI/WI to
 * per-tract CERTIFIED values that number is <= 1.0 by construction — so the
 * icon cannot fire there even when the underlying slot roll-up is over-
 * allocated. That is not a gap to be closed here: the certified value really is
 * what the cell means. The authoritative signal is the backend scan behind the
 * Over-Allocated Slots admin page, which reads the roll-up directly for exactly
 * this reason.
 *
 * @param {object} props - The displayed total and which measure it is.
 * @return {JSX.Element | null} A warning icon with an explanatory tooltip, or null.
 */
export const OverAllocationWarning: FC<{
  value: number | null | undefined;
  measure: 'NRI' | 'WI';
}> = ({ value, measure }) => {
  if (isNil(value) || value <= 1) return null;

  return (
    <FloatingTooltip
      content={`Total Slot ${measure} is ${formatVariable(
        value
      )} — above 1.0. A slot's interest across all owners cannot exceed 100%, so this unit is over-covered. Read the per-subtract UAT ${measure} rows instead, and see the Over-Allocated Slots admin page.`}
      placement="top"
    >
      <span
        className={styles.overAllocationWarning}
        aria-label={`Total Slot ${measure} exceeds 100%`}
      >
        <WarningAltFilled />
      </span>
    </FloatingTooltip>
  );
};

/**
 * Lays out a Total Slot interest cell — the formatted value, its warning, and
 * any source pill — on one line.
 *
 * @param {object} props - The cell's contents.
 * @return {JSX.Element} The laid-out cell.
 */
export const InterestCell: FC<{ children: ReactNode }> = ({ children }) => (
  <span className={styles.interestCell}>{children}</span>
);
